import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import classNames from 'classnames'
import moment from 'moment'

import MaskedInput from 'react-text-mask'
import { EyeIcon, EyeOffIcon } from 'lucide-react'
import { withAppFrame } from '~shared/components/AppFrameContext'
import { getMobileDateMask, convertToStandardFormat, getLocaleFormat } from './dateHelper'
import cls from './text-input-field.module.scss'
import Text from '@/shared/ui/Text/Text'
import { isValidEmailAddress } from '@/shared/lib/emailValidation'

const TextInputField = forwardRef((props, ref) => {
  const [fieldValue, setFieldValue] = useState(props.value)
  const [showPassword, setShowPassword] = useState(false)
  const inputRef = useRef(null)

  const blur = useCallback(() => {
    if (inputRef.current) inputRef.current.blur()
  }, [])

  const focus = useCallback(() => {
    if (inputRef.current) inputRef.current.focus()
  }, [])

  const getValue = useCallback(() => {
    const { data, required } = props
    const { type, size } = data

    const isRequired = required ?? data?.required
    const value = fieldValue ?? ''

    if (!isRequired && value === '') {
      return undefined
    }

    switch (type) {
      case 'tel':
        return value.replace(/[^+\d]/g, '')
      case 'date':
        if (size === 'xs') return convertToStandardFormat(value)
        return value
      default:
        return value
    }
  }, [fieldValue, props])

  const checkValidity = useCallback(() => {
    const { data, appFrame } = props
    const { type, required, max, minAge, maxAge } = data

    if (type === 'email') {
      const value = getValue() || ''
      return !required && !value ? true : isValidEmailAddress(value)
    }

    const valid = inputRef.current && inputRef.current.checkValidity()
    if (!valid) return false

    if (type === 'tel') {
      const phoneRgx = appFrame?.settings?.language?.phoneRgx

      const value = getValue() || ''
      if ((required || value.length > 0) && phoneRgx) {
        const rgx = new RegExp(phoneRgx)
        return rgx.test(value)
      }
    }

    if (type === 'date') {
      const value = getValue()
      const momentDate = moment(value)

      if (!momentDate.isValid()) return false

      if (max === 'now') {
        if (momentDate.isAfter(moment())) return false
      }

      if (maxAge > 0) {
        const minMoment = moment().subtract(maxAge, 'years')

        if (momentDate.isBefore(minMoment)) return false
      }

      if (minAge > 0) {
        const maxMoment = moment().subtract(minAge, 'years')

        if (momentDate.isAfter(maxMoment)) return false
      }
    }

    return true
  }, [getValue, props])

  const imperativeApi = useMemo(
    () => ({
      blur,
      focus,
      checkValidity,
      getValue,
    }),
    [blur, focus, checkValidity, getValue],
  )

  useImperativeHandle(ref, () => imperativeApi, [imperativeApi])

  useImperativeHandle(props.innerRef, () => imperativeApi, [imperativeApi])

  const getInputMask = useCallback(() => {
    let mask

    const { data, appFrame } = props
    const { type, mask: maskProp, size } = data

    if (Array.isArray(maskProp)) {
      mask = maskProp
    } else if (type === 'tel') {
      const maskSettings = appFrame?.settings?.language?.phoneMask

      if (Array.isArray(maskSettings)) mask = maskSettings
    } else if (type === 'date' && size === 'xs') {
      mask = getMobileDateMask()
    }

    if (mask) return mask.map(char => (char === '#' ? /\d/ : char))
  }, [props])

  const handleChange = useCallback(
    event => {
      setFieldValue(event.target.value)
      const { onChange } = props
      onChange && onChange(event)
    },
    [props],
  )

  const handleMouseDownPassword = useCallback(event => {
    event.preventDefault()
  }, [])

  const handleClickShowPassword = useCallback(() => {
    setShowPassword(prevState => !prevState)
  }, [])

  const {
    data,
    hideButtonId,
    value,
    className,
    invalid,
    onBlur,
    onFocus,
    tabIndex,
    tipRender,
    fullWidth,
    disabled,
    isExternal,
  } = props

  const {
    id,
    readOnly,
    placeholder,
    required,
    pattern,
    multiline,
    type,
    rows,
    max,
    maxLength,
    size,
  } = data

  const inputId = data.id || id
  const errorId = `${id || inputId || 'text-input'}-error-text`
  const supportingTextId = `${id || inputId || 'text-input'}-supporting-text`
  const isDisabled = readOnly || disabled

  let errorText
  if (props.errorText) {
    errorText = props.errorText
  } else if (!isExternal) {
    errorText = data.errorText
  }

  const showErrorText = errorText && invalid
  const showSupportingText = data.helperText && !showErrorText

  const isPassword = data.type === 'password'
  const inputMask = getInputMask()

  let defaultType = type
  let defaultInputMode = 'text'
  let inputPlaceholder

  if (isPassword && showPassword) defaultType = 'text'

  if (type === 'email') {
    defaultType = 'text'
    defaultInputMode = 'email'
  }

  if (type === 'date' && size === 'xs') {
    defaultType = 'text'
    defaultInputMode = 'numeric'
    inputPlaceholder = getLocaleFormat()
  }

  let defaultMax
  if (max === 'now' && type === 'date') {
    defaultMax = convertToStandardFormat(new Date())
  }

  const inputClassName = classNames(
    cls.inputElement,
    { [cls.passwordBullets]: isPassword && !showPassword && inputRef?.current?.value },
    isPassword && !showPassword && multiline ? cls.inputTextMulti : cls.inputText,
  )
  const inputProps = {
    id: inputId,
    name: data.name || data.id,
    defaultValue: value,
    onChange: handleChange,
    onBlur,
    onFocus,
    onKeyUp: event => event.keyCode === 27 && event.target.blur(),
    tabIndex,
    pattern,
    maxLength,
    placeholder: inputPlaceholder,
    className: inputClassName,
    disabled: isDisabled,
    required,
    'aria-describedby': showErrorText ? errorId : showSupportingText ? supportingTextId : undefined,
  }

  const inputElement = inputMask ? (
    <TextMaskCustom
      {...inputProps}
      max={defaultMax}
      inputMode={defaultInputMode}
      inputMask={inputMask}
      inputRef={inputRef}
    />
  ) : multiline ? (
    <textarea
      {...inputProps}
      rows={rows || 4}
      ref={node => {
        inputRef.current = node
      }}
    />
  ) : (
    <input
      {...inputProps}
      placeholder={placeholder}
      type={defaultType}
      max={defaultMax}
      inputMode={defaultInputMode}
      ref={node => {
        inputRef.current = node
      }}
    />
  )

  return (
    <div
      key={id}
      className={classNames(cls.root, className, {
        [cls.fullWidth]: fullWidth,
        [cls.invalid]: invalid && !isDisabled,
        [cls.disabled]: isDisabled,
      })}
    >
      <div className={classNames(cls.inputShell, { [cls.withAdornment]: isPassword })}>
        {inputElement}
        {isPassword ? (
          <button
            type="button"
            className={cls.visibilityButton}
            id={hideButtonId || `hidePassword_${id}`}
            aria-label="Toggle password visibility"
            onClick={handleClickShowPassword}
            onMouseDown={handleMouseDownPassword}
            disabled={isDisabled}
          >
            {showPassword ? <EyeOffIcon /> : <EyeIcon />}
          </button>
        ) : null}
      </div>

      {showErrorText && (
        <Text id={errorId} className={cls.helperText}>
          {errorText}
        </Text>
      )}

      {showSupportingText && (
        <Text id={supportingTextId} className={cls.supportingText}>
          {data.helperText}
        </Text>
      )}

      {tipRender && tipRender({ className: cls.tipBox })}
    </div>
  )
})

const TextMaskCustom = props => {
  const { inputRef, inputMask, placeholder, ...other } = props

  return (
    <MaskedInput
      {...other}
      ref={node => {
        inputRef.current = node ? node.inputElement : null
      }}
      mask={inputMask}
      placeholderChar={'\u2000'}
      placeholder={
        placeholder ||
        inputMask.map(maskChar => (typeof maskChar === 'object' ? '\u2000' : maskChar)).join('')
      }
    />
  )
}

export { TextInputField }
export default withAppFrame(TextInputField)
